import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {ComponentEvent, StateAwareComponent} from 'src/app/model/rest/base';
import {catchError, map, switchMap} from 'rxjs/operators';
import {concat, forkJoin, Observable, of} from 'rxjs';
import {HttpEventType, HttpResponse} from '@angular/common/http';
import {PublicV3RestService} from "../../services/rest/v3/public-rest.service";
import {FileV3RestService} from "../../services/rest/v3/file-rest.service";
import {CountryDefinition, Gender, V3PersonDetails, V3PersonalSensitiveDetails} from "../../model/v3/people";
import {ManagerColV3RestService} from "../../services/rest/v3/manager-col-v3-rest.service";
import {PeopleV3RestService} from "../../services/rest/v3/people-rest.service";
import {V3FileItem} from "../../model/v3/file";

export class TeacherPersonalProfileFormComponentEvent extends ComponentEvent {
  static ET_FORM_EDITABLE = 'underEdit';
  static ET_FORM_READ_ONLY = 'readOnly';

  static fromEditableFlag(editable: boolean) {
    const res = new TeacherPersonalProfileFormComponentEvent();
    if (editable) {
      res.eventType = this.ET_FORM_EDITABLE;
    } else {
      res.eventType = this.ET_FORM_READ_ONLY;
    }
    return res;
  }
}

@Component({
    selector: 'app-teacher-personal-profile-form',
    templateUrl: './teacher-personal-profile-form.component.html',
    styleUrls: ['./teacher-personal-profile-form.component.css'],
    standalone: false
})
export class TeacherPersonalProfileFormComponent extends StateAwareComponent<TeacherPersonalProfileFormComponentEvent> implements OnInit {

  _teacherId: number;
  genders = [ 'Male', 'Female' ];
  personDetails: V3PersonDetails = {name: '', surname: ''};
  sensitiveDetails: V3PersonalSensitiveDetails = {
    gender: Gender.Female,
    nationality: '',
    birthDate: null,
    biography: '',
    studyReason: ''
  };
  profileEmail = '';
  profilePhoto: V3FileItem | null = null;
  nationalityIso = '';
  countryIso = '';
  personId: number | null = null;
  _editable = true;
  uploadProgress: number = null;

  @ViewChild('fileInput', {static: false}) fileInput;
  countries: CountryDefinition[];
  _schoolId: number;

  constructor(
    private fileV3Rest: FileV3RestService,
    private managerV3Rest: ManagerColV3RestService,
    private peopleV3Rest: PeopleV3RestService,
    publicV3Rest: PublicV3RestService) {
    super();
    publicV3Rest.listCountries().subscribe(countries => this.keepCountries(countries));
  }

  @Input()
  set teacherId(teacherId: number) {
    this._teacherId = teacherId;
    this.loadProfile();
  }

  @Input()
  set schoolId(schoolId: number) {
    this._schoolId = schoolId;
    this.loadProfile();
  }

  keepCountries(countries: CountryDefinition[]): void {
    this.countries = countries.concat().sort((l, r) => l.name.localeCompare(r.name));
  }

  @Input()
  set editable(editable: boolean) {
    this._editable = editable;
    this.stateEvent.emit(TeacherPersonalProfileFormComponentEvent.fromEditableFlag(this._editable));
  }

  public openFileChoose() {
    if (!this._editable) { return; }
    this.fileInput.nativeElement.click();
  }
  get editable() {
    return this._editable;
  }

  public getProfilePhoto() {
    if (!this.profilePhoto?.publicAccessUrl) {
      return '/assets/img/user-ml.png';
    }
    return `url(${this.profilePhoto.publicAccessUrl})`;
  }

  public onProfileImageUpload(target: EventTarget) {
    const input = target as HTMLInputElement;
    this.uploadProgress = 0;
    this.fileV3Rest.uploadFile(input.files[0]).subscribe(event => {
      if (event.type === HttpEventType.UploadProgress ) {
        this.uploadProgress = Math.round(100 * event.loaded / event.total);
      } else if (event instanceof HttpResponse) {
        this.profilePhoto = event.body as V3FileItem;
        this.uploadProgress = null;
      }
    }, () => this.uploadProgress = null);
  }

  public onSave() {
    this.editable = false;
    if (!this.personId) {
      return;
    }
    const personId = this.personId;
    const nationalityName = this.countries?.find(c => c.code === this.nationalityIso)?.name ?? '';
    const countryName = this.countries?.find(c => c.code === this.countryIso)?.name ?? '';

    const sensitive: V3PersonalSensitiveDetails = {
      gender: this.sensitiveDetails.gender,
      nationality: nationalityName,
      birthDate: this.sensitiveDetails.birthDate,
      biography: this.sensitiveDetails.biography,
      studyReason: this.sensitiveDetails.studyReason,
    };

    // Run the updates one after another (not in parallel) so they don't race. updatePerson is
    // last because it triggers a downstream broadcast of the whole person — running it after the
    // other casa updates ensures the broadcast reflects the final state and doesn't clobber them.
    const orderedUpdates: Observable<unknown>[] = [
      this.peopleV3Rest.updateSensitive(personId, sensitive),
      this.peopleV3Rest.getAddress(personId).pipe(
        switchMap((address) => this.peopleV3Rest.updateAddress(personId, {...address, country: countryName}))
      ),
    ];
    if (this.profilePhoto) {
      orderedUpdates.push(this.peopleV3Rest.updatePhoto(personId, this.profilePhoto));
    }
    orderedUpdates.push(this.peopleV3Rest.updatePerson(personId, {
      name: this.personDetails.name,
      surname: this.personDetails.surname,
    }));

    concat(...orderedUpdates.map((update) => update.pipe(catchError((err) => {
      console.error('profile update failed', err);
      return of(null);
    }))))
      .subscribe({complete: () => this.loadProfileAsManager()});
  }

  loadProfile(): any {
    this.loadProfileAsManager();
  }

  loadProfileAsManager() {
      if (this._teacherId == null
      || this._schoolId == null) {
        return;
      }
      this.managerV3Rest.getTeacher(this._schoolId, this._teacherId).pipe(
        map(teacher => teacher.person),
        switchMap((person) => forkJoin({
          person: of(person),
          sensitive: this.peopleV3Rest.getSensitive(person.id),
          photo: this.peopleV3Rest.getPhoto(person.id),
          address: this.peopleV3Rest.getAddress(person.id),
        })),
      ).subscribe(({person, sensitive, photo, address}) => {
        this.personId = person?.id ?? null;
        this.personDetails = {
          name: person?.details?.name ?? '',
          surname: person?.details?.surname ?? '',
        };
        this.profileEmail = person?.registrationEmail ?? '';
        this.sensitiveDetails = {
          gender: sensitive?.gender ?? Gender.Female,
          nationality: sensitive?.nationality ?? '',
          birthDate: sensitive?.birthDate ?? null,
          biography: sensitive?.biography ?? '',
          studyReason: sensitive?.studyReason ?? ''
        };
        this.nationalityIso = this.countries?.find(c => c.name === sensitive?.nationality)?.code ?? '';
        this.countryIso = this.countries?.find(c => c.name === address?.country)?.code ?? '';
        this.profilePhoto = photo ?? null;
        this.stateEvent.emit(ComponentEvent.initialized());
      });
  }

  onBirthDateChange(birthDate: any) {
    this.sensitiveDetails.birthDate = birthDate ? new Date(birthDate).getTime() : null
  }

  ngOnInit() {
    this.editable = false;
  }
}
